/**
 * Dagre auto-layout for the lineage graph ("Tidy" toolbar action).
 *
 * No React / React Flow imports — pure, unit-tested in isolation, called from the
 * island's Tidy handler. Only TOP-LEVEL nodes are repositioned: a group moves as a
 * whole (children keep their in-group coords), and edges touching a group member are
 * lifted to that member's top-level ancestor so groups arrange by their connections.
 */
import dagre from 'dagre';

const DEFAULT_W = 180;
const DEFAULT_H = 100;
const GROUP_W = 240;
const GROUP_H = 160;

const sizeOf = (node) => ({
  width: node.width || (node.kind === 'group' ? GROUP_W : DEFAULT_W),
  height: node.height || (node.kind === 'group' ? GROUP_H : DEFAULT_H),
});

/** Walk the parentId chain to the outermost (top-level) ancestor id. */
const topAncestor = (byId, id) => {
  let cur = byId.get(id);
  const seen = new Set();
  while (cur && cur.parentId && byId.has(cur.parentId) && !seen.has(cur.id)) {
    seen.add(cur.id);
    cur = byId.get(cur.parentId);
  }
  return cur ? cur.id : id;
};

/**
 * @param {Array} nodes map.nodes
 * @param {Array} edges map.edges
 * @param {{rankdir?:string, nodesep?:number, ranksep?:number}} [opts]
 * @return {Array<{id:string, position:{x:number,y:number}}>} new positions for
 *   top-level nodes only (empty when there are none).
 */
export const layoutGraph = (nodes, edges, {rankdir = 'LR', nodesep = 40, ranksep = 90} = {}) => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const topNodes = nodes.filter((n) => !n.parentId);
  if (topNodes.length === 0) return [];

  const g = new dagre.graphlib.Graph();
  g.setGraph({rankdir, nodesep, ranksep, marginx: 20, marginy: 20});
  g.setDefaultEdgeLabel(() => ({}));
  topNodes.forEach((n) => g.setNode(n.id, sizeOf(n)));

  const seen = new Set();
  (edges || []).forEach((e) => {
    const a = topAncestor(byId, e.source);
    const b = topAncestor(byId, e.target);
    const key = `${a} ${b}`;
    if (a === b || !g.hasNode(a) || !g.hasNode(b) || seen.has(key)) return;
    seen.add(key);
    g.setEdge(a, b);
  });

  dagre.layout(g);
  // dagre returns node centres; React Flow positions are top-left corners.
  return topNodes.map((n) => {
    const {x, y} = g.node(n.id);
    const {width, height} = sizeOf(n);
    return {id: n.id, position: {x: Math.round(x - width / 2), y: Math.round(y - height / 2)}};
  });
};
